import React from 'react';
import { Translate } from 'react-localize-redux-dep-updated';
import { useDispatch } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import {
  FlashMessage,
  Heading,
  IconDoor,
  IconElevator,
  InputTileOption,
  InputTiles,
  SheetLayoutContent,
  SheetLayoutContentWrapper,
  SheetLayoutHeader,
} from '../../../brenger-shared-ui';
import { useTypedSelector } from '../../../hooks';
import { RootState, StopType } from '../../../types';
import { floorAsString } from '../../../utils/floors';
import { translate } from '../../../utils/localization';
import { priceAsString } from '../../../utils/price';
import { getIsExcludingVat } from '../../User/ducks';
import FooterLayout from '../containers/FooterLayout';
import { actions, getPickupDestinationDetails } from '../ducks';
import { proceed } from '../proceed';

export interface FloorChoice {
  floor_level: number;
  elevator: boolean;
}

export const SubStepFloor: React.FC<RouteComponentProps> = ({ location }) => {
  const stopType = location.pathname.includes('/pickup/') ? StopType.PICKUP : StopType.DELIVERY;
  const dispatch = useDispatch();
  const floorChoice = useTypedSelector(state => state.generalTransport.choices?.[`${stopType}_floor`]);
  const isExcludingVat = useTypedSelector(getIsExcludingVat);

  const locality = useTypedSelector(getPickupDestinationDetails)?.locality;
  const error = useTypedSelector((state: RootState) => state.generalTransport.error);

  React.useEffect(() => {
    dispatch(actions.setProgress(5));
    dispatch(actions.resetChoice([`${stopType}_floor`]));
    dispatch(actions.getPrice());
  }, [stopType, location.pathname]);

  const rawFloorOptions =
    useTypedSelector(state => state.generalTransport.quote?.service_advisory?.[`${stopType}_floor_level_options`]) ||
    [];
  const groundLevelFee = rawFloorOptions.find(option => option.value === 0)?.price_change[
    isExcludingVat ? 'excl_vat' : 'incl_vat'
  ].amount;
  const elevatorFee = rawFloorOptions.find(option => option.value === 'elevator')?.price_change[
    isExcludingVat ? 'excl_vat' : 'incl_vat'
  ].amount;
  const basementFee = rawFloorOptions.find(option => option.value === -1)?.price_change[
    isExcludingVat ? 'excl_vat' : 'incl_vat'
  ].amount;

  /**
   * Ground level and elevator options
   */
  const options: InputTileOption<FloorChoice>[] = [
    {
      value: {
        floor_level: 0,
        elevator: false,
      },
      title: String(translate(`request_flow.${stopType}.situation_house.ground_floor`, { locality: { locality } })),
      desc: String(
        translate(`request_flow.${stopType}.situation_house.ready_at_front_door`, { locality: { locality } })
      ),
      suffix: <span className="text-primary">{priceAsString({ price: { amount: groundLevelFee } })}</span>,
      prefix: <IconDoor />,
    },
    {
      value: {
        floor_level: 0,
        elevator: true,
      },
      title: String(
        translate(`request_flow.${stopType}.situation_house.inside_with_elevator`, { locality: { locality } })
      ),
      desc: String(
        translate(`request_flow.${stopType}.situation_house.inside_with_elevator_subtitle`, { locality: { locality } })
      ),
      suffix: (
        <div>
          <span className="text-primary">{priceAsString({ price: { amount: elevatorFee } })}</span>
        </div>
      ),
      prefix: <IconElevator />,
    },
    {
      value: {
        floor_level: -1,
        elevator: false,
      },
      title: String(translate(`request_flow.situation.x_basement_v2`)),
      desc: translate(`request_flow.situation.floor_without_elevator`),
      suffix: (
        <div>
          <span className="text-primary">{priceAsString({ price: { amount: basementFee } })}</span>
        </div>
      ),
      prefix: <span className="floor-indication custom">-1</span>,
    },
  ];

  /**
   * Floor level options
   */
  for (let i = 1; i <= 10; i++) {
    const fee =
      rawFloorOptions.find(option => option.value === i)?.price_change[isExcludingVat ? 'excl_vat' : 'incl_vat']
        .amount || 0;
    options.push({
      value: {
        floor_level: i,
        elevator: false,
      },
      title: String(translate(`request_flow.situation.x_floor_v2`, { floor: floorAsString(i) })),

      desc: translate(`request_flow.situation.floor_without_elevator`),
      suffix: (
        <div>
          <span className="text-primary">{priceAsString({ price: { amount: fee } })}</span>
        </div>
      ),
      prefix: <span className="floor-indication custom">{i}</span>,
    });
  }

  return (
    <SheetLayoutContentWrapper>
      <SheetLayoutHeader>
        <Heading size={2} extraClasses={'no-margin'}>
          <Translate
            id={`request_flow.${stopType}.situation_house.question`}
            data={{ locality: <span className={'text-primary'}>{locality}</span> }}
          />
        </Heading>
        <div>
          <Translate id={'request_flow.insurance.description'} />
        </div>
      </SheetLayoutHeader>
      <SheetLayoutContent>
        <div className={'question-wrapper'}>
          <InputTiles
            value={floorChoice}
            id={`${stopType}.floor`}
            name={`${stopType}.floor`}
            options={options}
            onChange={(newFloorChoice: FloorChoice) => {
              dispatch(actions.setChoice({ [`${stopType}_floor`]: newFloorChoice }));
              proceed();
            }}
          />
        </div>
        {!!error && <FlashMessage type={'error'} message={error} />}
      </SheetLayoutContent>
      <FooterLayout customSubmitFunction={proceed} />
    </SheetLayoutContentWrapper>
  );
};
